/**
 * cond 游戏字典 → `{ game_id → meta }` 映射
 *
 * 各业务页 cond key 不同（materials / creative_trends / ugc_trends），
 * 统一用 `useCondGameMap` 拉取，避免在页面 utils 里重复写 useFilterCondData + useMemo。
 */
import { useMemo } from 'react';
import { useFilterCondData } from '@opm/components/FilterCommon/hooks/useFilterCondData';
import type { GameData } from '@opm/components/FilterCommon/constants/games';
/** cond 游戏数据 + 可选 release_date */
export interface IGameMeta extends GameData {
  release_date?: string;
}

export type TGameMap = Record<string, IGameMeta>;

/** 各页面 FilterCommon / 列表关联用的 cond 游戏 key */
export const COND_GAME_KEYS = {
  MATERIALS: 'materials_config_games',
  CREATIVE_TRENDS: 'creative_trends_config_games',
  UGC_TRENDS: 'ugc_trends_config_games',
} as const;

export type TCondGameKey = (typeof COND_GAME_KEYS)[keyof typeof COND_GAME_KEYS];

export type TUseGameMapResult = {
  gameMap: TGameMap;
  loading: boolean;
};

export function buildGameMapFromCond(
  games: IGameMeta[] | undefined | null,
): TGameMap {
  const m: TGameMap = {};
  (games || []).forEach(g => {
    if (g?.game_id) m[g.game_id] = g;
  });
  return m;
}

/**
 * 按 cond request key 拉游戏元信息映射
 * @param condKey - 如 `creative_trends_config_games`
 */
export function useCondGameMap(condKey: TCondGameKey): TUseGameMapResult {
  const { condData, loading } = useFilterCondData({
    keys: [],
    extrRequestKeys: [condKey],
  });

  const gameMap = useMemo<TGameMap>(
    () => buildGameMapFromCond(condData?.[condKey] as IGameMeta[] | undefined),
    [condData, condKey],
  );

  return { gameMap, loading };
}

export function useCTGameMap(): TUseGameMapResult {
  return useCondGameMap(COND_GAME_KEYS.CREATIVE_TRENDS);
}

export function useUGCGameMap(): TUseGameMapResult {
  return useCondGameMap(COND_GAME_KEYS.UGC_TRENDS);
}

/** materials_config_games — Competitor Ad Comparison 等模块 */
export function useGameMap(): TUseGameMapResult {
  return useCondGameMap(COND_GAME_KEYS.MATERIALS);
}
